/**
 * A REFERENCE — an entity the person picked with @, as a message carries it.
 *
 * Three forms, one fact:
 *   · in the message TEXT, a link at the spot it was typed: `[@Conveyor PLC](ref:controller/gw-1/plc-7)`.
 *     That is what renders as a chip, survives copy, fork and branch, and shows the model where in
 *     the sentence the person pointed;
 *   · on the archived message, a `data-reference` part carrying the CARD — what the model was told
 *     about the entity when the message was sent — so a reload, a fork or a branch replays the same
 *     facts rather than whatever is true by then;
 *   · in the PROMPT, the cards as one block after the text, the same way attachments are described.
 *
 * The wire format — the link's grammar, the part's shape, and the limits the archive enforces — is
 * defined once, in `@vitesse/agent-loop/references`, because the worker reads it too. It is
 * re-exported here so the browser imports it from one place. This file adds the browser's side:
 * reading a reference in (`toReference`), the archived part, and what the model is told.
 *
 * Pure: the composer, the transcript writer, the fold, the markdown renderer and the prompt mirror
 * all read it, and none of them may disagree about the shape.
 */
import { REFERENCE_CARD_MAX_CHARS, REFERENCE_FIELD_MAX_CHARS, referenceProblem, referenceToken, } from '@vitesse/agent-loop/references';
export { REF_LINK_SOURCE, mentionText, parseRefHref, plainRefs, refHref, referenceToken, } from '@vitesse/agent-loop/references';
/** The most references one message carries. A message is a question, not an inventory. */
export const MAX_REFERENCES = 10;
/** A card cut to its ceiling. Cut rather than refused: a long model description is not worth a
 *  failed message. */
function clampCard(card) {
    const one = card.trim();
    return one.length > REFERENCE_CARD_MAX_CHARS ? `${one.slice(0, REFERENCE_CARD_MAX_CHARS - 1)}…` : one;
}
/**
 * Anything that may be a reference — a provider's item, a draft read back from storage, a stored
 * part, a link with no part — as one the archive accepts, or null.
 *
 * The one way in, so every reader applies the same rules. A missing label falls back to the id, a
 * missing card is empty, and a label or card that is too long is cut. What cannot be cut — a kind
 * that is not a word, a missing id, an id that is too long — is not a reference. It is null here,
 * not a message the archive later refuses whole.
 */
export function toReference(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const d = raw;
    if (typeof d.kind !== 'string' || typeof d.id !== 'string')
        return null;
    const ref = {
        kind: d.kind,
        id: d.id,
        ...(typeof d.gatewayId === 'string' && d.gatewayId ? { gatewayId: d.gatewayId } : {}),
        label: (typeof d.label === 'string' && d.label ? d.label : d.id).slice(0, REFERENCE_FIELD_MAX_CHARS),
        card: clampCard(typeof d.card === 'string' ? d.card : ''),
    };
    return referenceProblem(ref) ? null : ref;
}
/**
 * What the MODEL is told about a message's references, appended to the message text in the prompt.
 *
 * One block, after the text, in the same bracketed form attachments use. Worded so it reads as a fact
 * about the message rather than an instruction from the person; the instruction to use the ids
 * directly is in the standing prompt, where it is cached once.
 *
 * Each card ends with the entity's own link, ready to copy. With the link only in the person's text,
 * a model asked to "link to it" wrote a page path of its own (`/models/line-controller`) — which
 * names no relay, so the app's router sent the click to the home page.
 */
export function referenceProse(refs) {
    const lines = refs.filter((r) => r.card.trim()).map((r) => `- ${r.card.trim()} — link: ${referenceToken(r)}`);
    if (!lines.length)
        return '';
    return `[Referenced with @ — the exact entities the person picked:\n${lines.join('\n')}]`;
}
/** The message as the model reads it: its text, then its references. */
export function withReferenceProse(text, refs) {
    const prose = refs?.length ? referenceProse(refs) : '';
    return prose ? `${text}\n\n${prose}` : text;
}
/** The archived part for one reference. */
export function referencePart(r) {
    return {
        type: 'data-reference',
        data: { kind: r.kind, id: r.id, ...(r.gatewayId ? { gatewayId: r.gatewayId } : {}), label: r.label, card: r.card },
    };
}
/** The references a stored message carries, in order. Tolerant like the other part readers: a part
 *  that is not a well-formed reference is skipped rather than failing the whole message. */
export function referencesOf(content) {
    const parts = Array.isArray(content) ? content : content?.parts;
    if (!Array.isArray(parts))
        return [];
    const out = [];
    for (const raw of parts) {
        const r = raw?.type === 'data-reference' ? toReference(raw.data) : null;
        if (r)
            out.push(r);
    }
    return out;
}
/** One key per entity, so the same thing mentioned twice is one reference. */
export const referenceKey = (r) => `${r.kind}\u0000${r.gatewayId ?? ''}\u0000${r.id}`;
//# sourceMappingURL=references.js.map